import { useEffect, useState } from "react";
import { Menu, X, Phone, ArrowRight } from "lucide-react";
import { SITE } from "@/lib/site";
import { Button } from "@/components/ui/button";

const NAV = [
  { href: "#servizi", label: "Servizi" },
  { href: "#nord-italia", label: "Nord Italia" },
  { href: "#sicurezza", label: "Sicurezza" },
  { href: "#recensioni", label: "Recensioni" },
  { href: "#contatti", label: "Contatti" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl border-b border-brand-line shadow-[0_6px_30px_-20px_rgba(10,23,31,0.25)]"
          : "bg-white/40 backdrop-blur-sm"
      }`}
    >
      <div className={`max-w-7xl mx-auto px-4 lg:px-8 flex items-center justify-between transition-all duration-500 ${scrolled ? "h-[68px] lg:h-[88px]" : "h-[78px] lg:h-[108px]"}`}>
        <a href="#top" className="flex items-center gap-3 lg:gap-4" data-testid="header-logo-link">
          <img
            src={SITE.logoUrl}
            alt="Sicilia Traslochi"
            className={`object-contain transition-all duration-500 drop-shadow-sm ${scrolled ? "h-12 w-12 lg:h-20 lg:w-20" : "h-14 w-14 lg:h-24 lg:w-24"}`}
            loading="eager"
          />
          <div className="leading-tight hidden sm:block">
            <div className={`font-display font-extrabold tracking-tight text-brand-ink transition-all ${scrolled ? "text-base" : "text-lg lg:text-xl"}`}>
              SICILIA TRASLOCHI
            </div>
            <div className="text-[10px] tracking-[0.22em] uppercase text-brand-secondary mt-0.5">
              Movimentiamo il futuro · dal 2007
            </div>
          </div>
          {/* Tiny mobile brand */}
          <div className="sm:hidden leading-tight">
            <div className="font-display font-extrabold text-sm tracking-tight text-brand-ink">
              SICILIA TRASLOCHI
            </div>
            <div className="text-[9px] tracking-[0.2em] uppercase text-brand-secondary mt-0.5">
              Dal 2007
            </div>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((item) => (
            <a
              key={item.href}
              href={item.href}
              className="link-underline text-sm font-semibold text-brand-ink/85 hover:text-brand-primary transition-colors"
              data-testid={`nav-${item.href.replace("#", "")}-link`}
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={`tel:${SITE.phoneTel}`}
            className="flex items-center gap-2 text-sm font-bold text-brand-primary"
            data-testid="header-phone-link"
          >
            <Phone className="w-4 h-4" />
            {SITE.phone}
          </a>
          <Button
            asChild
            className="bg-brand-primary hover:bg-brand-ink text-white rounded-full px-6 h-12 font-bold shadow-lg shadow-brand-primary/20"
            data-testid="header-cta-btn"
          >
            <a href="#preventivo">
              Preventivo Gratuito
              <ArrowRight className="ml-2 w-4 h-4" />
            </a>
          </Button>
        </div>

        <button
          aria-label="Apri menu"
          className="lg:hidden p-2 rounded-md text-brand-ink"
          onClick={() => setOpen((v) => !v)}
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-brand-line animate-fade-in" data-testid="mobile-menu">
          <div className="px-5 py-5 space-y-3">
            {NAV.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setOpen(false)}
                className="block py-2 text-base font-semibold text-brand-ink"
                data-testid={`mobile-nav-${item.href.replace("#", "")}-link`}
              >
                {item.label}
              </a>
            ))}
            <div className="flex gap-2 pt-3">
              <Button asChild variant="outline" className="flex-1 rounded-full h-11 border-brand-primary text-brand-primary">
                <a href={`tel:${SITE.phoneTel}`}>
                  <Phone className="w-4 h-4 mr-2" /> Chiama
                </a>
              </Button>
              <Button asChild className="flex-1 rounded-full h-11 bg-brand-primary text-white">
                <a href="#preventivo" onClick={() => setOpen(false)}>Preventivo</a>
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
